import Decimal from 'decimal.js'

import { formatUsd } from '@/lib/decimal-format'
import { compactZeroRuns } from '@/lib/utils/compact-leading-zeros'

/**
 * Display-number preferences and the one formatter every user-facing number goes through.
 *
 * Formatting itself always runs in en-US (grouping ",", decimal ".") — the format the rest
 * of the codebase and its tests assume — and the chosen separator style is applied as a
 * final re-punctuation step. That keeps rounding identical across styles and lets strings
 * from older en-US helpers be localized with `text()` instead of being rewritten.
 */

export type SeparatorStyle = 'comma-dot' | 'dot-comma' | 'space-comma' | 'apostrophe-dot' | 'plain'

export const SEPARATOR_STYLES: Record<SeparatorStyle, { group: string; decimal: string }> = {
  'comma-dot': { group: ',', decimal: '.' },
  'dot-comma': { group: '.', decimal: ',' },
  // Narrow no-break space: the SI/ISO grouping mark, and it never wraps mid-number.
  'space-comma': { group: ' ', decimal: ',' },
  'apostrophe-dot': { group: '’', decimal: '.' },
  plain: { group: '', decimal: '.' },
}

export interface NumberFormatPrefs {
  separators: SeparatorStyle
}

export const DEFAULT_NUMBER_FORMAT: NumberFormatPrefs = { separators: 'comma-dot' }

/** The separator style a locale writes numbers in ("de-DE" → dot-comma, "de-CH" →
 *  apostrophe-dot), so users who never open the setting still type what they're used to. */
export function detectSeparatorStyle(locale?: string): SeparatorStyle {
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(1234567.5)
    const group = parts.find((p) => p.type === 'group')?.value ?? ''
    const decimal = parts.find((p) => p.type === 'decimal')?.value
    if (decimal === ',') return /\s/.test(group) ? 'space-comma' : 'dot-comma'
    if (decimal === '.' && /['’]/.test(group)) return 'apostrophe-dot'
  } catch {
    // An unknown locale tag: fall through to the default.
  }
  return DEFAULT_NUMBER_FORMAT.separators
}

/** The initial setting before the user picks one: the browser's locale, or the default on the
 *  server (nothing is stored until the user chooses, so it keeps following the browser). */
export function localeNumberFormat(): NumberFormatPrefs {
  if (typeof navigator === 'undefined') return DEFAULT_NUMBER_FORMAT
  return { separators: detectSeparatorStyle(navigator.language) }
}

/** Shown in place of an account-level amount while balances are hidden. */
export const MASK = '****'

const MISSING = '—'

/** Re-punctuate a string produced by an en-US number formatter. */
export function localizeSeparators(enUs: string, style: SeparatorStyle): string {
  if (style === 'comma-dot') return enUs
  const { group, decimal } = SEPARATOR_STYLES[style]
  return enUs.replace(/[,.]/g, (c) => (c === ',' ? group : decimal))
}

const intlCache = new Map<string, Intl.NumberFormat>()
function enUsFormat(options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const key = JSON.stringify(options)
  let format = intlCache.get(key)
  if (!format) {
    format = new Intl.NumberFormat('en-US', options)
    intlCache.set(key, format)
  }
  return format
}

export interface FormatOptions extends Intl.NumberFormatOptions {
  /** Account-level amount (balance, size, PnL): masked while balances are hidden. */
  sensitive?: boolean
}

export interface NumberFormatter {
  /** Whether balances are hidden, for surfaces that hide more than a single string. */
  hidden: boolean
  style: SeparatorStyle
  /** A number (or numeric string) via Intl options; missing/non-finite renders "—". */
  number(value: number | string | null | undefined, options?: FormatOptions): string
  /** USD, 2 dp. A string is treated as an exact Decimal and never goes through Number(). */
  usd(value: number | string | null | undefined, options?: { sensitive?: boolean }): string
  /** A string some other en-US formatter already produced (formatQty, a chart label). */
  text(enUs: string, options?: { sensitive?: boolean }): string
}

export function createNumberFormatter(
  prefs: NumberFormatPrefs,
  hidden: boolean,
  {
    compactZeros = false,
  }: {
    /** Show a fraction opening with 3+ zeros as "0.0₃42" (see compactZeroRuns). Tables. */
    compactZeros?: boolean
  } = {}
): NumberFormatter {
  const style = SEPARATOR_STYLES[prefs.separators] ? prefs.separators : 'comma-dot'
  const finish = (enUs: string, sensitive: boolean | undefined) =>
    sensitive && hidden
      ? MASK
      : localizeSeparators(compactZeros ? compactZeroRuns(enUs) : enUs, style)

  return {
    hidden,
    style,
    number(value, { sensitive, ...intl } = {}) {
      const n = typeof value === 'string' ? (value.trim() === '' ? null : Number(value)) : value
      if (n == null || !Number.isFinite(n)) return finish(MISSING, sensitive)
      return finish(enUsFormat(intl).format(n), sensitive)
    },
    usd(value, { sensitive } = {}) {
      if (typeof value === 'string') return finish(formatUsd(value), sensitive)
      if (value == null || !Number.isFinite(value)) return finish(MISSING, sensitive)
      const enUs = enUsFormat({
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value)
      return finish(enUs === '-$0.00' ? '$0.00' : enUs, sensitive)
    },
    text(enUs, { sensitive } = {}) {
      return finish(enUs, sensitive)
    },
  }
}

/** Characters people type for a style's grouping mark that we treat as that mark. */
const GROUP_ALIASES: Partial<Record<SeparatorStyle, RegExp>> = {
  'space-comma': /[  ]/g,
  'apostrophe-dot': /'/g,
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

/**
 * Turn what the user typed into a number field back into a canonical en-US string with no
 * grouping ("1234.56"), or null to reject the keystroke. `previous` is what the field showed
 * before, so separators our own formatting put there can be edited around freely.
 *
 * Strict by design: a grouping mark the user *adds* is only accepted where the result is
 * validly grouped (a paste like "1.234,56"). So "1,5" under 1,234.56 or "1.5" under 1.234,56
 * is refused instead of silently becoming 15. Any character outside the style is refused.
 * A leading "-" is only accepted with `allowNegative` (a lone "-" survives so it can be typed);
 * `integer` refuses the decimal mark (whole-number fields such as seconds sent as int64).
 */
export function parseNumberInput(
  input: string,
  previous: string,
  style: SeparatorStyle,
  { allowNegative = false, integer = false }: { allowNegative?: boolean; integer?: boolean } = {}
): string | null {
  if (allowNegative && input.startsWith('-')) {
    const rest = parseNumberInput(input.slice(1), previous.replace(/^-/, ''), style, { integer })
    return rest === null ? null : `-${rest}`
  }
  const { group, decimal } = SEPARATOR_STYLES[style] ?? SEPARATOR_STYLES['comma-dot']
  const alias = GROUP_ALIASES[style]
  const normalize = (s: string) => (alias ? s.replace(alias, group) : s)
  const next = normalize(input)
  if (next === '') return ''

  const allowed = new RegExp(`^[\\d${escapeRegExp(group)}${escapeRegExp(decimal)}]*$`)
  if (!allowed.test(next)) return null

  const [whole, fraction, ...rest] = next.split(decimal)
  if (rest.length > 0 || (integer && fraction !== undefined)) return null
  if (!group) return fraction === undefined ? whole : `${whole}.${fraction}`
  if (fraction?.includes(group)) return null

  const count = (s: string) => s.split(group).length - 1
  if (count(next) > count(normalize(previous))) {
    const g = escapeRegExp(group)
    if (!new RegExp(`^\\d{1,3}(${g}\\d{3})*$`).test(whole)) return null
  }

  const digits = whole.split(group).join('')
  return fraction === undefined ? digits : `${digits}.${fraction}`
}

/** True when `value` (as the field's owner stored it) is the number `canonical` spells, only
 *  less of it: "1." or "1.50" stored as "1"/"1.5", or "-"/"-0" stored as empty/zero. */
/** For a keystroke parseNumberInput refused: did the user add a "," or "." that isn't this
 *  style's decimal mark? That's someone typing their usual decimal mark in the wrong style. */
export function addedForeignDecimalMark(input: string, previous: string, style: SeparatorStyle) {
  const { decimal } = SEPARATOR_STYLES[style] ?? SEPARATOR_STYLES['comma-dot']
  const count = (s: string, c: string) => s.split(c).length - 1
  return [',', '.'].some((c) => c !== decimal && count(input, c) > count(previous, c))
}

export function isTypingOf(canonical: string, value: string) {
  if (canonical === value) return true
  // A negative zero still being typed ("-", "-0", "-0.0") is stored as zero.
  if (/^-0*\.?0*$/.test(canonical)) return value === '' || value === '0' || value === '-0'
  return canonical.includes('.') && canonical.replace(/\.?0*$/, '') === value
}

/** A number as a plain decimal string for an editable field — never exponent notation
 *  (String(1e-7) is "1e-7", which the field can't parse back). */
export function plainNumber(n: number): string {
  return Number.isFinite(n) ? new Decimal(n).toFixed() : ''
}
